import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { orderAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import './OrderDetailPage.css';

const STATUS_STEPS = ['pending', 'confirmed', 'processing', 'shipped', 'out_for_delivery', 'delivered'];

const STATUS_COLORS = {
  pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
  shipped: '#06b6d4', out_for_delivery: '#f97316', delivered: '#22c55e',
  cancelled: '#ef4444', returned: '#6b7280',
};

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {

  const loadOrder = async () => {
    try {

      const res =
        await orderAPI.get(orderId);

      const trackingRes =
        await orderAPI.track(orderId);

      setOrder({
        ...res.data,
        tracking_data:
          trackingRes.data.tracking_data
      });

    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  loadOrder();

}, [orderId]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setCancelling(true);
    try {
      const res = await orderAPI.cancel(orderId);
      setOrder(res.data);
      toast.success('Order cancelled');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Cannot cancel order');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!order) return <div className="container page-wrapper"><p>Order not found.</p></div>;

  const currentStep = STATUS_STEPS.indexOf(order.status);
  const isCancelled = order.status === 'cancelled';

  return (
    <>
<Helmet>
  <title>{order?.order_id ? `Order ${order.order_id} – Vedonica International` : 'Vedonica International'}</title>
</Helmet>
      <div className="order-detail page-wrapper">
        <div className="container">
          <Link to="/dashboard?tab=orders" className="order-detail__back">
            <FiArrowLeft size={16} /> Back to Orders
          </Link>

          <div className="order-detail__header">
            <div>
              <h1 className="order-detail__id">Order #{order.order_id}</h1>
              <p className="order-detail__date">
                Placed on {new Date(order.created_at).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <span
              className="badge"
              style={{
                background: (STATUS_COLORS[order.status] || '#6b7280') + '22',
                color: STATUS_COLORS[order.status] || '#6b7280',
                fontSize: '0.85rem',
                padding: '8px 16px'
              }}
            >
              {order.status_display}
            </span>
          </div>

          {/* Status tracker */}
          {!isCancelled && (
            <div className="order-tracker card">
              <h3 className="order-tracker__title">Order Status</h3>
              <div className="order-tracker__steps">
                {STATUS_STEPS.map((step, i) => {
                  const done = i <= currentStep;
                  const active = i === currentStep;
                  return (
                    <React.Fragment key={step}>
                      <div className={`tracker-step ${done ? 'tracker-step--done' : ''} ${active ? 'tracker-step--active' : ''}`}>
                        <div className="tracker-step__dot">
                          {done && <FiCheck size={12} />}
                        </div>
                        <p className="tracker-step__label">
                          {step.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </p>
                      </div>
                      {i < STATUS_STEPS.length - 1 && (
                        <div className={`tracker-line ${i < currentStep ? 'tracker-line--done' : ''}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
 {/* Shiprocket Live Tracking */}
{order.awb_code ? (
  <>
    <div
      style={{
        marginTop: 20,
        padding: '16px 20px',
        background: '#f0faf4',
        borderRadius: 12,
        border: '1px solid #c1e8cf'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12
        }}
      >
        <div style={{ flex: 1, minWidth: 120 }}>
          <p
            style={{
              fontSize: 10,
              color: '#7aab8f',
              fontWeight: 600,
              marginBottom: 4
            }}
          >
            AWB / TRACKING NO.
          </p>

          <p
            style={{
              fontWeight: 700,
              color: '#1a5c3a',
              fontSize: 14
            }}
          >
            {order.awb_code}
          </p>
        </div>

        {order.courier_name && (
          <div style={{ flex: 1, minWidth: 120 }}>
            <p
              style={{
                fontSize: 10,
                color: '#7aab8f',
                fontWeight: 600,
                marginBottom: 4
              }}
            >
              COURIER
            </p>

            <p
              style={{
                fontWeight: 700,
                color: '#1a5c3a',
                fontSize: 14
              }}
            >
              {order.courier_name}
            </p>
          </div>
        )}

        {order.shipping_status && (
          <div style={{ flex: 1, minWidth: 120 }}>
            <p
              style={{
                fontSize: 10,
                color: '#7aab8f',
                fontWeight: 600,
                marginBottom: 4
              }}
            >
              SHIPPING STATUS
            </p>

            <p
              style={{
                fontWeight: 700,
                color: '#3d9970',
                fontSize: 14
              }}
            >
              {order.shipping_status}
            </p>
          </div>
        )}
      </div>
    </div>

    {/* Tracking Timeline */}
    {order?.tracking_data?.tracking_data
      ?.shipment_track_activities?.length > 0 && (
      <div
        style={{
          marginTop: 20,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 12,
          padding: 16
        }}
      >
        <h3 style={{ marginBottom: 15 }}>
          Tracking Timeline
        </h3>

        {order.tracking_data.tracking_data.shipment_track_activities.map(
          (item, index) => (
            <div
              key={index}
              style={{
                borderLeft: '3px solid #22c55e',
                paddingLeft: '12px',
                marginBottom: '12px'
              }}
            >
              <strong>{item.activity}</strong>

              {item.location && (
                <div
                  style={{
                    fontSize: '12px',
                    color: '#666'
                  }}
                >
                  {item.location}
                </div>
              )}

              <div
                style={{
                  fontSize: '12px',
                  color: '#999'
                }}
              >
                {item.date}
              </div>
            </div>
          )
        )}
      </div>
    )}
  </>
) : order.tracking_number ? (
  <p className="order-tracker__tracking">
    Tracking: <strong>{order.tracking_number}</strong>
  </p>
) : null}
            </div>
          )}

          <div className="order-detail__layout">
            {/* Items */}
            <div>
              <div className="card order-items">
                <h3 className="order-items__title">Items Ordered</h3>
                {order.items?.map(item => (
                  <div key={item.id} className="order-item">
                    <div className="order-item__info">
                      <p className="order-item__name">{item.product_name}</p>
                      <p className="order-item__meta">Pack of {item.pack_size} × {item.quantity}</p>
                    </div>
                    <div className="order-item__pricing">
                      <p className="order-item__price">₹{Number(item.price).toFixed(2)}</p>
                      <p className="order-item__subtotal">Total: ₹{Number(item.subtotal).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Shipping */}
              <div className="card" style={{ marginTop: 20 }}>
                <h3 className="order-items__title">Delivery Address</h3>
                <p style={{ fontWeight: 600 }}>{order.shipping_name}</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 4 }}>{order.shipping_phone}</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 6, lineHeight: 1.6 }}>
                  {order.shipping_address_line1}
                  {order.shipping_address_line2 && <>, {order.shipping_address_line2}</>}<br />
                  {order.shipping_city}, {order.shipping_state} – {order.shipping_pincode}
                </p>
              </div>
            </div>

            {/* Summary */}
            <div>
              <div className="card order-summary-card">
                <h3 className="order-items__title">Payment Summary</h3>
                <div className="order-summary-card__row">
                  <span>Subtotal</span><span>₹{Number(order.subtotal).toFixed(2)}</span>
                </div>
                <div className="order-summary-card__row">
                  <span>Shipping</span>
                  <span>{Number(order.shipping_charge) === 0 ? 'FREE' : `₹${Number(order.shipping_charge).toFixed(2)}`}</span>
                </div>
                {Number(order.discount_amount) > 0 && (
                  <div className="order-summary-card__row" style={{ color: 'var(--green-medium)' }}>
                    <span>Discount</span><span>–₹{Number(order.discount_amount).toFixed(2)}</span>
                  </div>
                )}
                <div className="divider" />
                <div className="order-summary-card__row order-summary-card__row--total">
                  <span>Total</span><span>₹{Number(order.total_amount).toFixed(2)}</span>
                </div>
                <div style={{ marginTop: 16, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  <p>Payment: <strong style={{ color: 'var(--text-primary)' }}>{order.payment_method?.toUpperCase()}</strong></p>
                  <p style={{ marginTop: 4 }}>Status: <strong style={{ color: order.payment_status === 'paid' ? 'var(--green-medium)' : 'var(--text-primary)' }}>
                    {order.payment_status_display}
                  </strong></p>
                  {order.coupon_code && <p style={{ marginTop: 4 }}>Coupon: <strong>{order.coupon_code}</strong></p>}
                </div>
              </div>

              {/* Cancel */}
              {['pending', 'confirmed'].includes(order.status) && (
                <button
                  className="btn"
                  style={{ width: '100%', marginTop: 12, color: '#dc2626', border: '1px solid #fecaca', borderRadius: 'var(--radius-md)' }}
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Order'}
                </button>
              )}

              {/* Status history */}
              {order.status_history?.length > 0 && (
                <div className="card" style={{ marginTop: 20 }}>
                  <h3 className="order-items__title">Order Timeline</h3>
                  <div className="order-timeline">
                    {order.status_history?.map(h => (
                      <div key={h.id} className="timeline-item">
                        <div className="timeline-item__dot" />
                        <div>
                          <p className="timeline-item__status">
                            {h.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                          </p>
                          {h.note && <p className="timeline-item__note">{h.note}</p>}
                          <p className="timeline-item__date">
                            {new Date(h.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetailPage;
